import Cookies from 'js-cookie';
import locales from 'locales';
import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { UserContext } from 'stores';
import {
  JWT_EXPIRATION_BUFFER,
  JWT_EXPIRATION_LOCAL_STORAGE_KEY,
} from '../../constants';
import BannerSection from './BannerSection';
import LoginFormSection from './LoginFormSection';
import TwoFactorAuthModal from './TwoFactorAuthModal';
import { PageContainer } from './styles';

function LoginPage() {
  const browserLanguage = ['nl', 'en'].includes(navigator.language)
    ? navigator.language
    : 'nl';
  const userStore = useContext(UserContext);
  const [passwordValue, setPasswordValue] = useState('');
  const [usernameValue, setUsernameValue] = useState('');
  const [hasLoginError, setHasLoginError] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(
    convertToSpecificLocale(browserLanguage)
  );

  useEffect(() => {
    const checkToken = async () => {
      // Load the token from the localStorage
      const jwtExpiry = Cookies.get(JWT_EXPIRATION_LOCAL_STORAGE_KEY);

      if (!jwtExpiry) {
        // No expiry has been stored, we will do nothing
        return;
      }

      // calculate the time left until the token expires (in seconds)
      const jwtExpirationDuration =
        parseInt(jwtExpiry, 10) - Math.floor(Date.now() / 1000);
      // If we have a refresh token stored & the jwt is about to expire, then we will refresh the token
      if (jwtExpirationDuration < JWT_EXPIRATION_BUFFER) {
        try {
          // Refresh the JWT
          await userStore.loginRefresh();

          // Load the data for the user
          await userStore.loadData();
        } catch (error) {
          console.error('error logging in', error);
          // Something went wrong refreshing the jwt. We log the user out
          userStore.logout();
        }
        return;
      }

      if (userStore.validateToken()) {
        // we have a valid token, so we'll load the data for the user
        await userStore.loadData();
      }
    };

    checkToken();
  }, [userStore]);

  const handleSubmit = async () => {
    setHasLoginError(false);
    try {
      await userStore.login(usernameValue, passwordValue);
    } catch (e) {
      setHasLoginError(true);
    }
  };

  function convertToSpecificLocale(language) {
    const languageMap = {
      en: 'en-US',
      nl: 'nl-NL',
      // Add more language mappings as needed
    };

    return languageMap[language] || language; // Return the mapped value or the original language if not found
  }

  const handleChangeLanguage = (language) => {
    setSelectedLanguage(language);
  };

  return (
    <IntlProvider locale={browserLanguage} messages={locales[selectedLanguage]}>
      <React.Fragment>
        <TwoFactorAuthModal visible={!!userStore.loginRequestType} />
        <PageContainer>
          <BannerSection />
          <LoginFormSection
            usernameValue={usernameValue}
            passwordValue={passwordValue}
            onUsernameInput={setUsernameValue}
            onPasswordInput={setPasswordValue}
            onSubmit={handleSubmit}
            hasLoginError={hasLoginError}
            defaultLanguage={selectedLanguage}
            handleChangeLanguage={handleChangeLanguage}
          />
        </PageContainer>
      </React.Fragment>
    </IntlProvider>
  );
}

const Login = observer(LoginPage);

export default Login;
